'use client';
import { cn } from '@/app/lib';
import React, { useEffect, useMemo, useState } from 'react';
import { Icon } from '../Icon';
import { useMediaQuery } from '@workspace/hooks';
import { Typography } from '../Typography';

interface AccordionSection {
  id: string;
  title: React.ReactNode;
  content: React.ReactNode;
}

interface AccordionProps {
  sections: AccordionSection[];
  className?: string;
  openIds?: string[];
  onChange?: (next: string[]) => void;
  allowMultiple?: boolean;
}

export function AccordionWithCustomContent({
  sections,
  className,
  openIds,
  onChange,
  allowMultiple = false,
}: AccordionProps) {
  // ?INFO: sanitized version of sections with guaranteed-unique keys for React rendering and accordion state management.
  const normalized = useMemo(() => {
    const seen = new Map<string, number>();
    return (sections || []).map((s, i) => {
      const count = (seen.get(s.id) ?? 0) + 1;
      seen.set(s.id, count);
      const __kid = count === 1 ? s.id : `${s.id}__${i}`;
      return { ...s, __kid };
    });
  }, [sections]);

  const allIds = useMemo(() => normalized.map((s) => s.__kid), [normalized]);
  const isControlled = openIds !== undefined;
  const [internalOpenIds, setInternalOpenIds] = useState<Set<string>>(() => new Set(allIds));
  const currentOpenIds = isControlled ? new Set(openIds) : internalOpenIds;

  useEffect(() => {
    if (!isControlled) {
      setInternalOpenIds(new Set(allIds));
    }
  }, [normalized, allIds, isControlled]);

  const toggle = (kid: string) => {
    let next: Set<string>;
    if (allowMultiple) {
      next = new Set(currentOpenIds);
      next.has(kid) ? next.delete(kid) : next.add(kid);
    } else {
      next = currentOpenIds.has(kid) ? new Set() : new Set([kid]);
    }
    if (!isControlled) setInternalOpenIds(next);
    onChange?.([...next]);
  };

  const isDesktop = useMediaQuery('(min-width: 768px)');
  const gapClass = (aOpen: boolean, bOpen: boolean) => {
    if (aOpen) return isDesktop ? 'h-2xl' : 'h-xl';
    if (!aOpen && !bOpen) return isDesktop ? 'h-xl' : 'h-lg';
    else return isDesktop ? 'h-xl' : 'h-lg';
  };

  return (
    <div className={cn('flex flex-col gap-0', className)}>
      {normalized.map(({ __kid, title, content }, i) => {
        const isOpen = currentOpenIds.has(__kid);
        const next = normalized[i + 1];
        const nextOpen = next ? currentOpenIds.has(next.__kid) : false;

        return (
          <React.Fragment key={__kid}>
            <div className="overflow-visible">
              <button
                id={`acc-button-${__kid}`}
                type="button"
                aria-expanded={isOpen}
                aria-controls={`acc-panel-${__kid}`}
                onClick={() => toggle(__kid)}
                className={cn(
                  'w-full flex items-center justify-between pt-5 pb-2 text-left',
                  isOpen ? 'border-b border-primary' : 'border-b border-platinum',
                )}
              >
                <Typography variant="h2" styleVariant="h5" className="text-black">
                  {title}
                </Typography>
                <span
                  className={cn(
                    'transform transition-transform duration-300 ease-in-out',
                    isOpen ? 'rotate-180' : 'rotate-0',
                  )}
                >
                  <Icon
                    name="ChevronDown"
                    size={24}
                    className={cn('transition-colors duration-200', isOpen ? 'text-primary' : 'text-gray-900')}
                  />
                </span>
              </button>

              {isOpen && (
                <div id={`acc-panel-${__kid}`} role="region" aria-labelledby={`acc-button-${__kid}`} hidden={!isOpen}>
                  <div className="md:mt-8 mt-4">{content}</div>
                </div>
              )}
            </div>

            {i < normalized.length - 1 && <div aria-hidden className={cn('shrink-0', gapClass(isOpen, nextOpen))} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}
